import { useEffect, useState } from "react";

interface CurrencyFieldData {
  currencyCode?: string;
  customCurrencyCode?: string;
  customCurrencyValue?: string;
}

/**
 * A meta value entered in a currency other than the column's gets converted
 * and persisted server-side (`data.customCurrencyCode`/`customCurrencyValue`
 * next to the converted `value`). Any external refresh of that same field
 * (a websocket-driven refetch, a reload, another tab) re-delivers the
 * converted `value` together with that snapshot - if a component just takes
 * the fresh `value` at face value, it shows the column-currency number under
 * the custom currency code. This hook re-derives both from the snapshot
 * whenever it's present, so every currency field stays consistent without
 * each caller re-implementing the same sync logic.
 */
export function useCellCurrencyState(
  fieldValue: any,
  data?: CurrencyFieldData,
) {
  const hasPersistedCustomCurrency = Boolean(
    data?.customCurrencyCode &&
      data?.customCurrencyValue !== undefined &&
      data?.customCurrencyValue !== null,
  );

  const [value, setValue] = useState<any>(
    hasPersistedCustomCurrency ? data!.customCurrencyValue : fieldValue,
  );
  const [cellCurrencyCode, setCellCurrencyCode] = useState<string>(
    (hasPersistedCustomCurrency
      ? data?.customCurrencyCode
      : data?.currencyCode) || "",
  );
  const [hasCustomCurrency, setHasCustomCurrency] = useState<boolean>(
    hasPersistedCustomCurrency,
  );

  useEffect(() => {
    if (
      data?.customCurrencyCode &&
      data?.customCurrencyValue !== undefined &&
      data?.customCurrencyValue !== null
    ) {
      setValue(data.customCurrencyValue);
      setCellCurrencyCode(data.customCurrencyCode);
      setHasCustomCurrency(true);
    } else {
      setValue(fieldValue);
      setHasCustomCurrency(false);
    }
  }, [fieldValue, data?.customCurrencyCode, data?.customCurrencyValue]);

  useEffect(() => {
    if (!hasCustomCurrency) {
      setCellCurrencyCode(data?.currencyCode || "");
    }
  }, [data?.currencyCode, hasCustomCurrency]);

  const handleCurrencyPickerSave = (newCurrencyCode: string) => {
    setCellCurrencyCode(newCurrencyCode);
    setHasCustomCurrency(true);
  };

  return {
    value,
    setValue,
    cellCurrencyCode,
    hasCustomCurrency,
    handleCurrencyPickerSave,
  };
}
